import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";

import AppContainer from "../../components/AppContainer";
import ProductShareButton from "../../components/Buttons/ProductShareButton";
import DM2BuyCarousel from "../../components/Carousel";
import Basket from "../../components/Cart/Basket";
import FilePicker from "../../components/FilePicker";
import Footer from "../../components/Footer/Footer";
import LoaderComponent from "../../components/Loader";
import NavBar from "../../components/Navbar";
import PackageExtraDetails from "../../components/OrderDetails/PackageExtraDetails";
import ProductCustomFields from "../../components/ProductCustomFields";
import ProductSizeFields from "../../components/ProductSizeFields";
import SideNavDrawer from "../../components/SideNavDrawer";
import SimilarProducts from "../../components/SimilarProducts";
import Toast from "../../components/Toast";
import { useStoreContext } from "../../contexts/StoreDataContext";
import { ogImage } from "../../lib/media";
import { cn } from "../../lib/utils";
import CartStorage from "../../services/frontend/CartStorage";
import { getProductClientSide } from "../../services/frontend/client-apis";
import { checkProductOrActiveVariantHasLowStock, sanitizeHtml } from "../../services/helper";
import { trackEvent } from "../../utils/analytics";
import { EVENTS } from "../../utils/analyticsConfig";
import NotFound from "../404";
import styles from "./product.module.css";

const ProductPage = () => {
  const router = useRouter();
  const { slug } = router.query;

  // context data
  const context = useStoreContext();
  const storeData = context?.storeData;
  const themeData = context?.themeData;
  const isProplanActive = context?.isProplanActive;
  const lowStockThreshold = storeData?.storePrefs?.lowStockThreshold;

  // Product states
  const productId = slug;
  const [product, setProduct] = useState(null);
  const [productUrl, setProductUrl] = useState("");
  const [productLoading, setProductLoading] = useState(true);
  const [productError, setProductError] = useState(null);

  // other states
  const [showCart, setShowCart] = useState(false);
  const [cart, setCart] = useState(null);
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedCustomAttributes, setSelectedCustomAttributes] = useState([]);
  const [focusActive, setFocusActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState([]);
  const [productData, setProductData] = useState(null);
  const [showLowStockAlert, setShowLowStockAlert] = useState(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const descriptionRef = useRef(null);

  const customFieldsRef = useRef(null);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) return;

      try {
        setProductLoading(true);
        const productResponse = await getProductClientSide(productId, storeData?.subdomain);

        if (productResponse.errorCode) {
          setProductError(productResponse.errorCode);
        } else {
          setProduct(productResponse.product);
          setProductUrl(productResponse.productUrl);
        }
      } catch (err) {
        console.error("Product fetch error:", err);
        setProductError(404);
      } finally {
        setProductLoading(false);
      }
    };

    fetchProduct();
  }, [productId, storeData?.subdomain]);

  // product_viewed is the top of the storefront funnel. Keyed on product.id so
  // it fires once per product, not on every re-render of the same page.
  useEffect(() => {
    if (!product?.id) return;
    trackEvent(EVENTS.PRODUCT_VIEWED, {
      product_id: product.id,
      product_name: product.name,
      product_price: product.price,
      product_quantity_available: product.quantity,
      product_has_variants: Boolean(product.variantOptions?.length),
      product_is_low_stock: checkProductOrActiveVariantHasLowStock(product, lowStockThreshold),
    });
  }, [product?.id]);

  useEffect(() => {
    let pdSizeVariantOptions = [];

    if (product) {
      pdSizeVariantOptions =
        product.variantOptions && product.variantOptions.length === 1
          ? product.variantOptions
          : product.variantOptions && product.variantOptions.length > 1
            ? product.variantOptions.filter((vop) => vop.type === "fit" && vop.availableStock > 0)
            : [];

      const customAttributes =
        product.customAttributes && product.customAttributes.length
          ? product.customAttributes.map((attribute) => {
              const attrib = { ...attribute };
              attrib.ref = React.createRef();
              return attrib;
            })
          : [];

      setSelectedSize(
        pdSizeVariantOptions && pdSizeVariantOptions.length ? pdSizeVariantOptions[0]?.name : "",
      );

      setProductData({
        ...product,
        allPhotos: product?.["productPhotos"],
        headerPhoto: product?.["productPhotos"][0],
        headerDescription: product?.headerDescription,
        shippingFee: product?.store?.["shippingFee"] || 0,
        shippingFeeCap: product?.store?.["shippingFeeCap"],
        quantity: 1, // set default product quantity to 1
        customAttributes: customAttributes,
      });
    }
  }, [product]);

  useEffect(() => {
    if (productData || selectedSize) {
      setShowLowStockAlert(
        checkProductOrActiveVariantHasLowStock({
          product: productData,
          variantName: selectedSize,
          lowStockThreshold,
        }),
      );
    }
  }, [productData, selectedSize, lowStockThreshold]);

  // compare with prevProductId
  useEffect(() => {
    initializeProduct();
  }, [productId]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        if (descriptionRef.current) {
          const element = descriptionRef.current;
          const overflow = element.scrollHeight > element.clientHeight;
          setHasOverflow(overflow);

          // Only show button if there's overflow
          if (!overflow) {
            setIsExpanded(true); // Auto-expand if no overflow
          }
        }
      }, 100);
    }
  }, [productData?.description, selectedSize]);

  // Reset overflow state when product changes
  useEffect(() => {
    setIsExpanded(false);
    // setHasOverflow(false);
  }, [productData?.id]);

  // Only run this useEffect when selectedSize actually changes
  useEffect(() => {
    // Only run if selectedSize has a value (not on initial load)
    if (selectedSize) {
      // Reset expanded state when size changes
      setIsExpanded(false);

      // Wait for DOM to update, then check overflow
      const timerId = window.setTimeout(() => {
        if (descriptionRef.current) {
          const element = descriptionRef.current;
          const overflow = element.scrollHeight > element.clientHeight;
          setHasOverflow(overflow);
        }
      }, 0);

      return () => window.clearTimeout(timerId);
    }
  }, [selectedSize]);

  const initializeProduct = async () => {
    const cartData = await CartStorage.initializeCart();
    setCart(cartData);

    const productArr = cartData.products.filter((productV) => productV.id === productId);
    if (productArr.length > 0) {
      let selectedCustomAttributesVal = productArr[0].customAttributes;

      const sizeVariantOptions =
        productArr[0].variantOptions && productArr[0].variantOptions.length
          ? productArr[0].variantOptions.filter((vop) => vop.type === "fit")
          : [];

      let productSizeVariantOptions = [];
      if (product) {
        productSizeVariantOptions =
          product.variantOptions && product.variantOptions.length
            ? product.variantOptions.filter((vop) => vop.type === "fit")
            : [];
      }

      let selectedSizeVal =
        sizeVariantOptions && sizeVariantOptions.length
          ? sizeVariantOptions[0]?.name
          : productSizeVariantOptions && productSizeVariantOptions.length
            ? productSizeVariantOptions[0]?.name
            : "";

      setSelectedSize(selectedSizeVal);
      setSelectedCustomAttributes(selectedCustomAttributesVal);
    }
    if (typeof window != "undefined") {
      window.document.body.style.scrollBehavior = "smooth";
      let inputs = window.document.querySelectorAll("input");
      inputs.forEach((input) => {
        input.onfocus = () => {
          setFocusActive(true);
        };
        input.onblur = () => {
          setTimeout(() => {
            setFocusActive(false);
          }, 2000);
        };
      });
    }
  };

  const updateAddedToCart = () => {};

  const findProductStockAndPrice = (size) => {
    // Only treat empty size as "sold out" if the product actually has variants
    if (
      !size &&
      product.variantOptions &&
      product.variantOptions.length > 0 &&
      product.variantOptions[0]?.availableStock === 0
    ) {
      size = "-";
      return { stockAvailable: 0 };
    }

    if (!size) {
      size = "-";
    }
    const variantPrice = product.variantOptions;
    const variantObj = {};

    if (!variantPrice || (variantPrice && !variantPrice.length)) {
      if (
        Object.prototype.hasOwnProperty.call(product, "availableStock") &&
        !Object.prototype.hasOwnProperty.call(product, "stockAvailable")
      ) {
        product.stockAvailable = product.availableStock;
      }
      return product;
    }
    variantPrice.forEach((variant) => {
      let vSize = variant?.type === "fit" ? variant?.name : "-";
      if (!variantObj[vSize]) {
        variantObj[vSize] = {
          price: variant?.price,
          stockAvailable: variant?.availableStock,
        };
      }
    });

    if (variantObj && variantObj[size]) {
      return variantObj[size];
    } else {
      return null;
    }
  };

  const sizeVariants = useMemo(() => {
    return product && product.variantOptions && product.variantOptions.length
      ? product.variantOptions.filter((vop) => vop.type === "fit")
      : [];
  }, [product]);

  const validated = async () => {
    let isValid = true;
    const isFocusAble = true;
    for (const ca of productData.customAttributes) {
      if (ca?.required === true || !ca) {
        if (!(await ca.ref?.current?.validate(isFocusAble))) {
          isValid = false;
        }
      }
    }
    return isValid;
  };

  const storeProductToCart = async () => {
    const customAttributes = [];
    for (const ca of productData.customAttributes) {
      if (ca.ref.current.state.inputValue.trim() !== "") {
        customAttributes.push({
          name: ca?.name,
          value: ca.ref.current.state.inputValue,
        });
      }
    }

    if (selectedSize) {
      productData.size = selectedSize;
    }

    let _product = { ...productData };
    _product.customAttributes = customAttributes;

    // For web: use attachmentLinkV2 (array)
    _product.attachmentLinkV2 = attachmentUrl;

    // For mobile compatibility: use attachmentLink (string - first file only)
    _product.attachmentLink = attachmentUrl.length > 0 ? attachmentUrl[0] : "";

    const cartVal = CartStorage.getCart();
    const updatedCart = JSON.parse(JSON.stringify(cartVal));

    const productIndex = updatedCart.products.findIndex(
      (item) => item.id === productData.id && item.size === productData.size,
    );
    if (productIndex !== -1) {
      _product.quantity = updatedCart.products[productIndex].quantity || 1;
      updatedCart.products[productIndex] = _product;
    } else {
      if (!_product.quantity) {
        _product.quantity = 1;
      }
      updatedCart.products.push(_product);
    }
    updatedCart.shippingFee =
      _product?.store?.shippingCharge || _product?.store?.shippingInfo?.shippingCharge || 0;
    updatedCart.shippingFeeCap = _product.shippingFeeCap;
    await CartStorage.saveCart(updatedCart);
  };

  const showToast = () => {
    try {
      setOpen(true);
      setTimeout(() => {
        setOpen(false);
      }, 3000);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Share operation was cancelled");
      }
    }
  };

  const addToCart = async () => {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "add_to_cart",
        category: "Button",
        action: "Click",
        label: "Add To Cart",
        product: productData.name,
      });
    }
    if (await validated(productData)) {
      if (typeof window != "undefined") {
        if (focusActive) {
          window.scrollTo(0, 0);
          window.document.body.scrollTop = 0;
        }
      }

      setTimeout(async () => {
        await storeProductToCart(productData);
        const currentCart = CartStorage.getCart();
        setCart(currentCart);
        trackEvent(EVENTS.PRODUCT_ADDED_TO_CART, {
          product_id: productData?.id,
          product_name: productData?.name,
          product_price: productData?.price,
          product_size: productData?.size,
          cart_item_count: currentCart?.products?.length ?? 0,
        });
        if (currentCart.products && currentCart.products.length > 0) {
          setTimeout(() => {
            setShowCart(true);
          }, 100);
        } else {
          setShowCart(true);
        }
        updateAddedToCart(product.id, true);
      }, 400);
    }
  };

  const handleReadLess = () => {
    setIsExpanded(false);

    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const elementTop = element.offsetTop;
      // const elementBottom = elementTop + element.offsetHeight;
      const viewportTop = window.pageYOffset;
      // const viewportBottom = viewportTop + window.innerHeight;

      // Check if element is off-screen (above viewport)
      const isOffScreen = elementTop < viewportTop;

      // Only scroll if element is off-screen
      if (isOffScreen) {
        const elementPosition = elementTop - 80; // 80px offset from top

        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });
      }
    }
  };

  const alreadyAddedToCart = useMemo(() => {
    if (cart && cart.products && cart.products.length > 0) {
      const productArr = cart.products.filter(
        (productV) => productV.id === productId && productV.size === selectedSize,
      );
      if (productArr.length > 0) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }, [cart, productId, selectedSize]);

  useEffect(() => {
    const productPrice = sizeVariants.length
      ? findProductStockAndPrice(selectedSize, product)?.price
      : product?.price;
    setProductData((prev) => ({
      ...prev,
      price: productPrice,
    }));
  }, [selectedSize]);

  const homePageEnabled = product?.store?.homePage?.homePageEnabled;

  const showSimilarProducts =
    Array.isArray(productData?.collectionV2) &&
    productData?.collectionV2?.length > 0 &&
    productData?.collectionV2[0]?._id &&
    productData?.id;

  const activeCollectionId = productData?.collectionV2?.find((col) => col.isActive)?._id;

  if (productLoading || !storeData) {
    return <LoaderComponent />;
  }

  if (productError === 404 || !product) {
    return <NotFound />;
  }

  return (
    <AppContainer>
      <div className="m-0 flex w-full max-w-[var(--max-width)] flex-col justify-around bg-background pt-[70px] text-fontBackground">
        <Head>
          <title>dm2buy</title>
          <meta name="description" content={productData.headerDescription} />
          <meta name="title" content={productData?.name} />
          <link rel="icon" href="https://dm2buy.blob.core.windows.net/dm2buy/favicon.ico" />
          <link
            href="https://fonts.googleapis.com/css2?family=Roboto:wght@700&family=Roboto:wght@400;700&display=swap"
            rel="stylesheet"
          />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1,user-scalable=0"
          />

          <meta property="og:type" content="website" />
          <meta property="og:title" content={productData?.name} />
          <meta property="og:description" content={productData?.headerDescription} />
          <meta
            property="og:image"
            content={ogImage(productData?.productPhotos?.[0], productData?.productPhotosV2)}
          />
          <meta property="og:site_name" content="dm2buy" />
          <meta property="og:url" content={productUrl} />

          <meta name="twitter:card" content="summary" />
          <meta name="twitter:url" content={productUrl} />
          <meta name="twitter:title" content={productData?.name} />
          <meta property="twitter:description" content={productData?.headerDescription} />
          <meta property="twitter:image" content={productData?.headerPhoto} />
        </Head>

        {cart?.products?.length ? (
          <Basket
            fromProductPage={true}
            isBasketOpen={showCart}
            setCart={(value) => setCart(value)}
            cartData={cart}
            updateAddedToCart={updateAddedToCart}
            setHideInAdvance={() => {}}
            handleShowCart={() => setShowCart(!showCart)}
            store={storeData}
            isProplanActive={isProplanActive}
          />
        ) : (
          ""
        )}

        <NavBar
          cartActive={cart?.products?.length ? true : false}
          handleShowCart={() => setShowCart(!showCart)}
          homeActive={homePageEnabled && homePageEnabled === true ? true : false}
          store={productData?.store}
          storeName={productData?.store?.storeName || ""}
          toggleSideNav={() => setSideNavOpen(!sideNavOpen)}
          theme={themeData}
        />

        <SideNavDrawer
          isOpen={sideNavOpen}
          toggleDrawer={() => setSideNavOpen(!sideNavOpen)}
          store={storeData}
        />

        <DM2BuyCarousel store={storeData} product={productData} />

        <div className={styles.productSub}>
          <div className={styles.productHead}>
            <h1 className={cn(styles.productHeading, "!text-fontBackground")}>
              {productData.name}
            </h1>

            {/* <ShareButton /> */}
            <ProductShareButton name={productData.name} toast={showToast} />
          </div>

          {showLowStockAlert && <div className="text-sm text-red-500">Only few left!</div>}

          {productData && <div className={styles.priceContainer}></div>}

          <PackageExtraDetails
            dispatchTime={
              productData?.store?.["dispatchTime"] ||
              productData?.store?.shippingInfo?.["dispatchTime"]
            }
            instaUserId={
              productData?.store?.instagramHandle ||
              productData?.store?.contactInfo?.instagramHandle
            }
            style={{
              margin: "20px 0",
            }}
          />

          {sizeVariants && sizeVariants.length ? (
            <ProductSizeFields
              theme={themeData}
              sizeVariants={sizeVariants}
              selectedSize={selectedSize}
              updateSelectedSize={(size) => setSelectedSize(size)}
            />
          ) : (
            ""
          )}

          {productData?.description && (
            <div className={styles.descriptionContainer}>
              <div className="">
                <div
                  ref={descriptionRef}
                  className={cn(
                    styles.description,
                    "break-words",
                    !isExpanded ? "line-clamp-5" : "min-h-16",
                  )}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(productData.description) }}
                />

                {/* Only show button if there's actual overflow */}
                {hasOverflow &&
                  (isExpanded ? (
                    <button
                      className="mt-2 cursor-pointer font-bold text-primary underline hover:text-primary"
                      onClick={handleReadLess}
                    >
                      Read less
                    </button>
                  ) : (
                    <span
                      className="cursor-pointer bg-background font-bold text-primary underline hover:text-primary"
                      onClick={() => setIsExpanded(true)}
                    >
                      Read more
                    </span>
                  ))}
              </div>
            </div>
          )}

          <ProductCustomFields
            theme={themeData}
            selectedCustomAttributes={selectedCustomAttributes}
            product={productData}
            ref={customFieldsRef}
          />
          <FilePicker
            showContainer={productData?.isAttachmentEnabled}
            attachmentUrl={attachmentUrl}
            setAttachmentUrl={(url) => setAttachmentUrl(url)}
            attachmentLoading={attachmentLoading}
            setIsAttachmentLoading={(loading) => setAttachmentLoading(loading)}
          />
          <div className={styles.callToAction}>
            {findProductStockAndPrice(selectedSize, productData)?.stockAvailable <= 0 ? (
              <button className={styles.soldOutButton}>Sold Out</button>
            ) : (
              <button
                id={`add-to-bag-btn`}
                className="mt-2 flex w-full justify-between rounded-2xl bg-primary px-6 py-4 text-fontPrimary"
                onClick={addToCart}
                disabled={attachmentLoading}
              >
                <div>{alreadyAddedToCart ? "Added to Bag" : "Add to Bag"}</div>
                <div>{`${String.fromCharCode(0x20b9)}${productData.price}`}</div>
              </button>
            )}
          </div>
        </div>

        {showSimilarProducts && isProplanActive && (
          <SimilarProducts collectionId={activeCollectionId} currentProductId={productData.id} />
        )}

        <Footer theme={themeData} />

        <Toast type="success" message="Link copied successfully" open={open} />
      </div>
    </AppContainer>
  );
};

export default ProductPage;
